import { useNavigate, useParams, useLocation } from '@tanstack/react-router'
import { useCallback } from 'react'
import { supportedLanguages, type SupportedLanguage } from '@/lib/i18n/init'

/**
 * Hook for locale-aware navigation in TanStack Router
 * Provides utilities to navigate while preserving the current locale
 */
export function useLocaleNavigation() {
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const location = useLocation()
  
  // Get current locale from route params, fallback to 'en'
  const currentLocale = (params as any)?.locale as SupportedLanguage || 'en'

  /**
   * Get a localized path by prepending the current locale to the given path
   * @param path - The path to localize (should start with '/')
   * @param locale - Optional locale override, defaults to current locale
   * @returns Localized path (e.g., '/en/about' or '/nl/about')
   */
  const getLocalizedPath = useCallback(
    (path: string, locale?: SupportedLanguage): string => {
      const targetLocale = locale || currentLocale
      
      // Ensure path starts with '/'
      const normalizedPath = path.startsWith('/') ? path : `/${path}`
      
      // If path is just '/', return localized root
      if (normalizedPath === '/') {
        return `/${targetLocale}`
      }
      
      // If path already contains a locale, replace it
      const pathSegments = normalizedPath.split('/').filter(Boolean)
      if (supportedLanguages.includes(pathSegments[0] as SupportedLanguage)) {
        pathSegments[0] = targetLocale
        return `/${pathSegments.join('/')}`
      }
      
      // Prepend locale to path
      return `/${targetLocale}${normalizedPath}`
    },
    [currentLocale]
  )

  /**
   * Navigate to a path while preserving the current locale
   * @param to - The path to navigate to
   * @param options - Navigation options
   */
  const navigateWithLocale = useCallback(
    (to: string, options?: { replace?: boolean; locale?: SupportedLanguage }) => {
      const localizedPath = getLocalizedPath(to, options?.locale)
      navigate({ to: localizedPath, replace: options?.replace })
    },
    [navigate, getLocalizedPath]
  )

  /**
   * Switch to a different locale while staying on the same page
   * @param newLocale - The locale to switch to
   */
  const switchLocale = useCallback(
    (newLocale: SupportedLanguage) => {
      if (newLocale === currentLocale) return
      
      // Get current path without locale
      const currentPath = location.pathname
      const pathSegments = currentPath.split('/').filter(Boolean)
      
      // Remove current locale from path
      if (supportedLanguages.includes(pathSegments[0] as SupportedLanguage)) {
        pathSegments.shift()
      }
      
      // Create new path with new locale
      const newPath = pathSegments.length > 0 
        ? `/${newLocale}/${pathSegments.join('/')}`
        : `/${newLocale}`
      
      navigate({ to: newPath, replace: true })
    },
    [currentLocale, location.pathname, navigate]
  )

  /**
   * Get the current path without the locale prefix
   * @returns Path without locale (e.g., '/about' from '/en/about')
   */
  const getPathWithoutLocale = useCallback((): string => {
    const pathSegments = location.pathname.split('/').filter(Boolean)
    
    if (supportedLanguages.includes(pathSegments[0] as SupportedLanguage)) {
      pathSegments.shift()
    }
    
    return pathSegments.length > 0 ? `/${pathSegments.join('/')}` : '/'
  }, [location.pathname])

  /**
   * Check if a given locale is currently active
   * @param locale - The locale to check
   * @returns True if the locale is currently active
   */
  const isLocaleActive = useCallback(
    (locale: SupportedLanguage): boolean => {
      return currentLocale === locale
    },
    [currentLocale]
  )

  return {
    currentLocale,
    getLocalizedPath,
    navigateWithLocale,
    switchLocale,
    getPathWithoutLocale,
    isLocaleActive,
    supportedLanguages,
  }
}
